/**
 * PenObjectDetector — verify a pen by finding a DARK ELONGATED MASS in
 * the hand ROI.
 *
 * v1 used gradient PCA (linearity of edge orientations) — sensitive but
 * also fired on paper edges and shadows. v2 (this version) finds the
 * largest dark connected mass and checks its elongation directly: a pen
 * is a thin dark line, so its bounding pixel cloud has very high aspect
 * ratio along its principal axis (PCA on pixel positions).
 *
 * Pipeline:
 *   1. Compute hand bbox from 21 landmarks, expanded 25 % to catch a
 *      pen sticking past the fingers.
 *   2. Sample to ~96 px-wide ROI.
 *   3. Grayscale.
 *   4. Threshold = min(30th percentile, absolute 110) — pen pixels are
 *      darker than skin/paper. Lower percentile = more selective for
 *      truly dark pixels even when hand+pen are well-lit.
 *   5. Flood-fill largest dark connected component.
 *   6. PCA on the (x, y) positions of that component to get principal
 *      and secondary variances. elongation = 1 − λ2/λ1.
 *   7. confidence = elongation × density score. Reports `present` when
 *      a sufficiently elongated and sufficiently large mass is found.
 */

export interface HandLandmark { x: number; y: number; z?: number }

export interface PenObjectResult {
  /** True when a sufficiently elongated dark mass is found in the hand. */
  present: boolean;
  /** 0..1 confidence — elongation × density. */
  confidence: number;
  /** Hand bbox in normalized coords (caller may draw a debug overlay). */
  handBbox: { x: number; y: number; w: number; h: number };
}

const ZERO: PenObjectResult = {
  present: false,
  confidence: 0,
  handBbox: { x: 0, y: 0, w: 0, h: 0 },
};

export function detectPenObject(
  landmarks: HandLandmark[] | null | undefined,
  video: HTMLVideoElement,
  offCanvas: HTMLCanvasElement,
): PenObjectResult {
  if (!landmarks || landmarks.length < 21) return ZERO;
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return ZERO;

  // ── Hand bbox (25% expansion to catch pen overhang) ───────
  let minX = 1, maxX = 0, minY = 1, maxY = 0;
  for (const p of landmarks) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const padX = (maxX - minX) * 0.25;
  const padY = (maxY - minY) * 0.25;
  minX = Math.max(0, minX - padX);
  maxX = Math.min(1, maxX + padX);
  minY = Math.max(0, minY - padY);
  maxY = Math.min(1, maxY + padY);
  const handBbox = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  if (handBbox.w < 0.04 || handBbox.h < 0.04) return ZERO;

  // ── Sample to small ROI for speed ────────────────────────
  const TARGET_W = 96;
  const aspect = handBbox.h / handBbox.w;
  const sw = TARGET_W;
  const sh = Math.max(32, Math.round(TARGET_W * aspect));
  if (offCanvas.width !== sw || offCanvas.height !== sh) {
    offCanvas.width = sw;
    offCanvas.height = sh;
  }
  const ctx = offCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return ZERO;
  ctx.drawImage(
    video,
    handBbox.x * vw, handBbox.y * vh,
    handBbox.w * vw, handBbox.h * vh,
    0, 0, sw, sh,
  );
  const img = ctx.getImageData(0, 0, sw, sh);

  // ── Grayscale ─────────────────────────────────────────────
  const gray = new Uint8Array(sw * sh);
  for (let i = 0; i < gray.length; i++) {
    const j = i * 4;
    gray[i] = Math.round(img.data[j] * 0.299 + img.data[j + 1] * 0.587 + img.data[j + 2] * 0.114);
  }

  // ── Dark threshold = min(30th percentile, 110) ────────────
  // Lower-percentile = darkest pixels only. Floor at 110 prevents falsely
  // calling moderately-lit skin tones "dark" in dim scenes.
  const sorted = Array.from(gray).sort((a, b) => a - b);
  const pctThresh = sorted[Math.floor(sorted.length * 0.30)] || 100;
  const thresh = Math.min(pctThresh, 110);
  const bin = new Uint8Array(sw * sh);
  for (let i = 0; i < gray.length; i++) bin[i] = gray[i] <= thresh ? 1 : 0;

  // ── Flood-fill largest dark component ─────────────────────
  const visited = new Uint8Array(sw * sh);
  let best: number[] = [];
  const stack: number[] = [];
  for (let y = 0; y < sh; y++) {
    for (let x = 0; x < sw; x++) {
      const idx = y * sw + x;
      if (bin[idx] !== 1 || visited[idx]) continue;
      const comp: number[] = [];
      stack.length = 0;
      stack.push(idx);
      while (stack.length > 0) {
        const cur = stack.pop()!;
        if (visited[cur]) continue;
        visited[cur] = 1;
        comp.push(cur);
        const cx = cur % sw;
        const cy = (cur - cx) / sw;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = cx + dx;
            const ny = cy + dy;
            if (nx < 0 || nx >= sw || ny < 0 || ny >= sh) continue;
            const nIdx = ny * sw + nx;
            if (bin[nIdx] === 1 && !visited[nIdx]) stack.push(nIdx);
          }
        }
      }
      if (comp.length > best.length) best = comp;
    }
  }

  // Need a meaningful number of dark pixels.
  if (best.length < 25) return { ...ZERO, handBbox };

  // ── PCA on positions (length vs width) ────────────────────
  let sumX = 0, sumY = 0;
  for (const p of best) {
    sumX += p % sw;
    sumY += (p - (p % sw)) / sw;
  }
  const meanX = sumX / best.length;
  const meanY = sumY / best.length;
  let cxx = 0, cxy = 0, cyy = 0;
  for (const p of best) {
    const px = p % sw;
    const py = (p - px) / sw;
    const dx = px - meanX;
    const dy = py - meanY;
    cxx += dx * dx;
    cxy += dx * dy;
    cyy += dy * dy;
  }
  cxx /= best.length;
  cxy /= best.length;
  cyy /= best.length;
  const trace = cxx + cyy;
  const det = cxx * cyy - cxy * cxy;
  const disc = Math.max(0, trace * trace / 4 - det);
  const sqrtDisc = Math.sqrt(disc);
  const lambda1 = trace / 2 + sqrtDisc;
  const lambda2 = Math.max(0.0001, trace / 2 - sqrtDisc);
  // Aspect-like ratio along principal axes. Pen is long+thin → λ1 ≫ λ2.
  const elongation = Math.max(0, Math.min(1, 1 - lambda2 / lambda1));

  // Density score: dark mass should be moderate fraction of ROI (not
  // 90% — that's a shadow, not a pen).
  const density = best.length / (sw * sh);
  const densityScore = density < 0.05 ? density / 0.05
    : density > 0.40 ? Math.max(0, 1 - (density - 0.40) / 0.30)
    : 1;

  // Aspect ratio of principal vs secondary axis. Pen is very thin
  // (>5:1) — phone held end-on can elongate but width is much greater
  // (~2:1). Use sqrt(λ1/λ2) as length/width proxy.
  const aspectLW = lambda1 > 0 ? Math.sqrt(lambda1 / lambda2) : 0;
  // 0522 19:04 video — phone showed elongation ≈ 0.85 (passes 0.80) but
  // aspect ≈ 2.5; pen has aspect ≥ 5. Require aspect>=4 to gate pens.
  const PEN_ASPECT_MIN = 4.0;

  const confidence = elongation * densityScore;
  const present = confidence >= 0.55 && elongation >= 0.80 && aspectLW >= PEN_ASPECT_MIN;

  return { present, confidence, handBbox };
}
